import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import surveyData from '../../../../../../../mock-data/survey_defaults.json';

const sd = surveyData.data;

const STAGES = ['compose', 'send', 'tracker'];

/* ---------- Progress rail ---------- */
function ProgressRail({ stage }) {
  const idx = STAGES.indexOf(stage);
  const labels = [
    { k: 'compose', n: '01', l: 'Compose' },
    { k: 'send',    n: '02', l: 'Send' },
    { k: 'tracker', n: '03', l: 'Tracker' },
  ];
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--ink)' }}>
      {labels.map((s, i) => (
        <div key={s.k} style={{
          flex: 1, padding: '10px 14px',
          borderRight: i < labels.length - 1 ? '1px solid var(--hairline)' : 0,
          background: i <= idx ? 'var(--paper-2)' : 'var(--paper)',
          opacity: i <= idx ? 1 : 0.55,
          display: 'flex', alignItems: 'baseline', gap: 8,
        }}>
          <span className="mono" style={{ fontSize: 10, color: i === idx ? 'var(--accent-ink)' : 'var(--ink-4)', letterSpacing: '0.06em' }}>{s.n}</span>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 15, letterSpacing: '-0.01em', color: i === idx ? 'var(--accent-ink)' : 'var(--ink-2)' }}>{s.l}</span>
          {i === idx && <span style={{ flex: 1, borderBottom: '1px solid var(--accent)', marginBottom: 2 }} />}
        </div>
      ))}
    </div>
  );
}

/* ---------- Stage 1 — Compose ---------- */
function ComposeStage({ subject, setSubject, body, setBody, onNext }) {
  const fieldStyle = {
    width: '100%', border: 'none', outline: 'none',
    background: 'transparent', padding: '6px 0',
    fontFamily: 'var(--sans)', fontSize: 13.5, color: 'var(--ink)',
    resize: 'none', lineHeight: 1.55,
  };
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div>
        <div className="mono tiny" style={{ color: 'var(--ink-3)', marginBottom: 6 }}>STEP 01 · COMPOSE EMAIL · ONE MESSAGE, EVERYONE GETS IT</div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.2, maxWidth: 680 }}>
          Write the email you'll send. Same message to all 30 recipients — friends, peers and bosses.
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 6, maxWidth: 680, lineHeight: 1.5 }}>
          Use <span className="mono" style={{ background: 'var(--accent-soft)', padding: '1px 5px' }}>{'{first_name}'}</span> and{' '}
          <span className="mono" style={{ background: 'var(--accent-soft)', padding: '1px 5px' }}>{'{your_name}'}</span> as placeholders.
        </div>
      </div>

      <div style={{ border: '1px solid var(--ink)', background: 'var(--paper)', display: 'grid', gridTemplateRows: 'auto auto auto 1fr' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderBottom: '1px solid var(--ink)', background: 'var(--paper-2)' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', border: '1px solid var(--ink)' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', border: '1px solid var(--ink)' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', border: '1px solid var(--ink)' }} />
          <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.06em', marginLeft: 8 }}>NEW MESSAGE · CHARACTER SURVEY</span>
          <span style={{ flex: 1 }} />
          <span className="mono tiny" style={{ color: 'var(--ink-4)', letterSpacing: '0.06em' }}>DRAFT</span>
        </div>
        <div style={{ padding: '4px 14px', borderBottom: '1px dashed var(--hairline)', display: 'grid', gridTemplateColumns: '54px 1fr', alignItems: 'center', gap: 10 }}>
          <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.06em' }}>FROM</span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Margaret Halberd <span style={{ color: 'var(--ink-4)' }}>&lt;margaret@halberd.co&gt;</span></span>
        </div>
        <div style={{ padding: '4px 14px', borderBottom: '1px solid var(--ink)', display: 'grid', gridTemplateColumns: '54px 1fr', alignItems: 'center', gap: 10 }}>
          <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.06em' }}>SUBJECT</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ ...fieldStyle, fontFamily: 'var(--serif)', fontSize: 17, letterSpacing: '0', color: 'var(--accent-ink)' }} />
        </div>
        <div style={{ padding: '14px 14px 4px' }}>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} style={{ ...fieldStyle, fontFamily: 'var(--serif)', fontSize: 14, lineHeight: 1.6, minHeight: 280 }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.06em' }}>PLACEHOLDERS</span>
          {['{first_name}', '{your_name}'].map((t) => (
            <span key={t} className="mono" style={{ fontSize: 11, padding: '3px 7px', background: 'var(--accent-soft)', color: 'var(--accent-ink)' }}>{t}</span>
          ))}
        </div>
        <button onClick={onNext} style={{ padding: '10px 18px', border: '1px solid var(--ink)', background: 'var(--ink)', color: 'var(--paper)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
          Continue → Recipients
        </button>
      </div>
    </div>
  );
}

/* ---------- Stage 2 — Send ---------- */
function ImportPanel({ children, onCancel }) {
  return (
    <div style={{ border: '1px solid var(--ink)', padding: '12px 14px', background: 'var(--paper)', display: 'grid', gap: 10 }}>
      {children}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="mono" style={{ fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--ink-3)', textTransform: 'uppercase' }}>Cancel</button>
      </div>
    </div>
  );
}

function ManualEntry({ onAdd, onClose }) {
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [group, setGroup] = useState('peers');
  const fieldStyle = { padding: '7px 10px', border: '1px solid var(--hairline)', background: 'var(--paper)', fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--ink)', outline: 'none' };
  const submit = () => {
    if (!name.trim()) return;
    onAdd({ name: name.trim(), relation: relation.trim() || 'Manually added', group });
    setName(''); setRelation('');
  };
  return (
    <div style={{ border: '1px solid var(--ink)', padding: '12px 14px', background: 'var(--paper)', display: 'grid', gap: 10 }}>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 16, color: 'var(--accent-ink)' }}>Add a contact</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px auto', gap: 8 }}>
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
        <input placeholder="Relation (e.g. Former CEO, Centrica)" value={relation} onChange={(e) => setRelation(e.target.value)} style={fieldStyle} />
        <select value={group} onChange={(e) => setGroup(e.target.value)} style={fieldStyle}>
          <option value="friends">friends</option>
          <option value="peers">peers</option>
          <option value="bosses">bosses</option>
        </select>
        <button onClick={submit} disabled={!name.trim()} style={{ padding: '7px 14px', border: '1px solid var(--ink)', background: name.trim() ? 'var(--ink)' : 'var(--paper-2)', color: name.trim() ? 'var(--paper)' : 'var(--ink-4)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: name.trim() ? 'pointer' : 'not-allowed' }}>Add</button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="mono" style={{ fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--ink-3)', textTransform: 'uppercase' }}>Done</button>
      </div>
    </div>
  );
}

function SendStage({ contacts, addContact, selected, setSelected, onBack, onSend }) {
  const GROUPS = ['friends', 'peers', 'bosses'];
  const [importOpen, setImportOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const totalSel = selected.size;

  const fakeImport = (provider) => {
    setBusy(true);
    const seeds = {
      outlook: [
        { name: 'Charlotte Vane', group: 'peers', relation: 'Outlook · DPO peer, ex-Diageo' },
        { name: 'Ravi Shenoy', group: 'bosses', relation: 'Outlook · CEO, current portfolio client' },
      ],
      gmail: [
        { name: 'Henry Aitchison', group: 'friends', relation: 'Gmail · Walking-group, since 2014' },
        { name: 'Daniel Werner', group: 'peers', relation: 'Gmail · Industry forum chair' },
      ],
    };
    setTimeout(() => {
      (seeds[provider] || []).forEach((c, i) => addContact({ ...c, id: `${provider}-${Date.now()}-${i}` }));
      setBusy(false);
      setImportOpen(null);
      setToast(`Imported contacts from ${provider}.`);
      setTimeout(() => setToast(null), 3000);
    }, 1100);
  };

  const handleCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = String(ev.target?.result || '').split(/\r?\n/).map((r) => r.trim()).filter(Boolean);
      rows.slice(rows[0]?.toLowerCase().includes('name') ? 1 : 0).forEach((row, i) => {
        const cells = row.split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));
        if (!cells[0]) return;
        const grp = ['friends', 'peers', 'bosses'].includes(cells[2]?.toLowerCase()) ? cells[2].toLowerCase() : 'peers';
        addContact({ id: `csv-${Date.now()}-${i}`, name: cells[0], group: grp, relation: cells[1] || `CSV · ${grp}` });
      });
      setImportOpen(null);
    };
    reader.readAsText(file);
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleGroup = (g) => {
    const ids = contacts.filter((c) => c.group === g).map((c) => c.id);
    const allOn = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allOn) ids.forEach((id) => next.delete(id)); else ids.forEach((id) => next.add(id));
    setSelected(next);
  };

  const primaryBtn = (disabled) => ({
    padding: '9px 16px', border: '1px solid var(--ink)',
    background: disabled ? 'var(--paper-2)' : 'var(--ink)',
    color: disabled ? 'var(--ink-4)' : 'var(--paper)',
    fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', justifySelf: 'start', cursor: disabled ? 'not-allowed' : 'pointer',
  });

  const ImportBtn = ({ k, label, sub }) => (
    <button onClick={() => setImportOpen(k)} style={{ display: 'grid', gridTemplateRows: 'auto auto', gap: 2, padding: '8px 12px', border: '1px solid var(--ink)', background: importOpen === k ? 'var(--ink)' : 'var(--paper)', color: importOpen === k ? 'var(--paper)' : 'var(--ink)', textAlign: 'left' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: 10.5, color: importOpen === k ? 'var(--paper)' : 'var(--ink-3)', fontFamily: 'var(--mono)', letterSpacing: '0.04em' }}>{sub}</span>
    </button>
  );

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="mono tiny" style={{ color: 'var(--ink-3)', marginBottom: 6 }}>STEP 02 · CHARACTER TRACKER 10 / 10 / 10</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.2, maxWidth: 640 }}>
            Pick the people. The split that works is ten friends, ten peers, ten bosses.
          </div>
        </div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em' }}>{totalSel} / {contacts.length} CONTACTS · ONE EMAIL</div>
      </div>

      <div style={{ border: '1px solid var(--ink)', padding: '10px 12px', display: 'grid', gap: 10, background: 'var(--paper-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>ADD CONTACTS</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Import from your address book or paste names manually.</span>
          </div>
          {toast && <span className="mono tiny" style={{ color: 'var(--pos)', letterSpacing: '0.06em' }}>✓ {toast}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ImportBtn k="outlook" label="◉ Outlook" sub="Sign in to Microsoft 365" />
          <ImportBtn k="gmail" label="✉ Gmail" sub="Sign in to Google" />
          <ImportBtn k="csv" label="↥ CSV" sub="Upload .csv file" />
          <ImportBtn k="manual" label="✍ Manual" sub="Type one in" />
        </div>

        {importOpen === 'outlook' && (
          <ImportPanel onCancel={() => setImportOpen(null)}>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 16, color: 'var(--accent-ink)' }}>Sign in to Outlook</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>Connect your Microsoft 365 account. We'll fetch your contacts (read-only).</div>
              <button disabled={busy} onClick={() => fakeImport('outlook')} style={primaryBtn(busy)}>{busy ? 'Connecting…' : 'Continue with Microsoft →'}</button>
              <div className="mono tiny" style={{ color: 'var(--ink-4)' }}>Permissions: contacts.read · no message access</div>
            </div>
          </ImportPanel>
        )}
        {importOpen === 'gmail' && (
          <ImportPanel onCancel={() => setImportOpen(null)}>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 16, color: 'var(--accent-ink)' }}>Sign in to Gmail</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>Connect your Google account. We'll fetch your contacts (read-only).</div>
              <button disabled={busy} onClick={() => fakeImport('gmail')} style={primaryBtn(busy)}>{busy ? 'Connecting…' : 'Continue with Google →'}</button>
              <div className="mono tiny" style={{ color: 'var(--ink-4)' }}>Permissions: contacts.readonly</div>
            </div>
          </ImportPanel>
        )}
        {importOpen === 'csv' && (
          <ImportPanel onCancel={() => setImportOpen(null)}>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 16, color: 'var(--accent-ink)' }}>Upload CSV</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                Three columns: <span className="mono">name, relation, group</span> — where group is <span className="mono">friends</span>, <span className="mono">peers</span> or <span className="mono">bosses</span>.
              </div>
              <input type="file" accept=".csv,text/csv" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsv(f); }} style={{ fontSize: 12, fontFamily: 'var(--mono)' }} />
            </div>
          </ImportPanel>
        )}
        {importOpen === 'manual' && (
          <ManualEntry
            onAdd={(c) => { addContact({ ...c, id: `m-${Date.now()}` }); setToast(`Added ${c.name}.`); setTimeout(() => setToast(null), 2200); }}
            onClose={() => setImportOpen(null)}
          />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {GROUPS.map((g) => {
          const list = contacts.filter((c) => c.group === g);
          const allOn = list.length > 0 && list.every((c) => selected.has(c.id));
          const selCount = list.filter((c) => selected.has(c.id)).length;
          return (
            <div key={g} style={{ border: '1px solid var(--ink)', display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0 }}>
              <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--ink)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--paper-2)' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 16, textTransform: 'capitalize', color: 'var(--accent-ink)' }}>{g}</div>
                <button onClick={() => toggleGroup(g)} className="mono" style={{ fontSize: 10, letterSpacing: '0.06em', color: 'var(--ink-3)', textTransform: 'uppercase', cursor: 'pointer' }}>
                  {allOn ? 'Clear' : 'Select all'} · {selCount}/{list.length}
                </button>
              </div>
              <div style={{ padding: '6px 0', maxHeight: 280, overflow: 'auto' }}>
                {list.length === 0 && (
                  <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--ink-4)', fontSize: 11.5, fontFamily: 'var(--mono)', letterSpacing: '0.04em' }}>NO {g.toUpperCase()} YET</div>
                )}
                {list.map((p) => {
                  const on = selected.has(p.id);
                  return (
                    <button key={p.id} onClick={() => toggle(p.id)} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 10, alignItems: 'start', padding: '7px 10px', width: '100%', textAlign: 'left', borderTop: '1px dashed var(--hairline)', background: on ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer' }}>
                      <span style={{ width: 14, height: 14, marginTop: 2, border: '1px solid var(--ink)', background: on ? 'var(--ink)' : 'transparent', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontFamily: 'var(--mono)', fontSize: 9 }}>{on ? '✓' : ''}</span>
                      <span>
                        <span style={{ fontFamily: 'var(--serif)', fontSize: 14, color: 'var(--accent-ink)', lineHeight: 1.2 }}>{p.name}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.3, marginTop: 2 }}>{p.relation}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <button onClick={onBack} className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' }}>← Edit email</button>
        <button onClick={onSend} disabled={totalSel === 0} style={{ padding: '10px 18px', border: '1px solid var(--ink)', background: totalSel === 0 ? 'var(--paper-2)' : 'var(--ink)', color: totalSel === 0 ? 'var(--ink-4)' : 'var(--paper)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: totalSel === 0 ? 'not-allowed' : 'pointer' }}>
          Send to {totalSel} →
        </button>
      </div>
    </div>
  );
}

/* ---------- Stage 3 — Tracker ---------- */
function Counter({ value }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    if (value === prev.current) return;
    const start = prev.current; const target = value;
    const startT = performance.now(); const dur = 380;
    let raf;
    const tick = (t) => {
      const k = Math.min(1, (t - startT) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (k < 1) { raf = requestAnimationFrame(tick); } else { prev.current = target; }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{display}</span>;
}

function ReplyRow({ contact, reply }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 30); return () => clearTimeout(t); }, []);
  const tone = { friends: '#FFF2B8', peers: '#D6E8C6', bosses: '#F9D6C4' }[contact.group];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 14, padding: '12px 14px', borderTop: '1px dashed var(--hairline)', opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(4px)', transition: 'opacity 380ms ease, transform 380ms ease' }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, background: tone, border: '1px solid var(--ink)' }} />
          <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.06em' }}>{contact.group.toUpperCase()}</span>
        </div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 14, color: 'var(--accent-ink)', lineHeight: 1.2 }}>{contact.name}</div>
        <div style={{ fontSize: 10.5, color: 'var(--ink-4)', lineHeight: 1.3, fontFamily: 'var(--mono)' }}>{contact.relation}</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignSelf: 'center' }}>
        {reply.text.split(/\n+/).map((kw, ki) => (
          <span key={ki} style={{ padding: '3px 8px', border: '1px solid var(--hairline)', background: 'var(--paper)', fontSize: 11.5, color: 'var(--ink-2)', fontFamily: 'var(--sans)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{kw}</span>
        ))}
      </div>
      <div className="mono tiny" style={{ color: 'var(--ink-4)', alignSelf: 'center' }}>JUST IN</div>
    </div>
  );
}

function TrackerStage({ contacts, selected, replies, replySpeed, arrived, onView, onReset }) {
  const sentList = contacts.filter((c) => selected.has(c.id));
  const GROUPS = ['friends', 'peers', 'bosses'];
  const totalSent = sentList.length;
  const totalBack = arrived.size;
  const allBack = totalBack === totalSent && totalSent > 0;
  const minToView = Math.max(3, Math.ceil(totalSent * 0.5));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="mono tiny" style={{ color: 'var(--ink-3)', marginBottom: 6 }}>STEP 03 · LIVE · REPLIES STREAM IN</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            <Counter value={totalBack} /> of {totalSent} replies in.
            {allBack && <span style={{ color: 'var(--pos)', marginLeft: 8 }}>· complete</span>}
          </div>
        </div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em' }}>SPEED · {replySpeed}× · {totalSent ? Math.round(totalBack / totalSent * 100) : 0}%</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {GROUPS.map((g) => {
          const sent = sentList.filter((c) => c.group === g).length;
          const back = sentList.filter((c) => c.group === g && arrived.has(c.id)).length;
          return (
            <div key={g} style={{ border: '1px solid var(--ink)', padding: '14px 12px', display: 'grid', gap: 6 }}>
              <div className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>{g.toUpperCase()}</div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 34, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--accent-ink)' }}>
                <Counter value={back} /><span style={{ color: 'var(--ink-4)', fontSize: 16 }}>/{sent}</span>
              </div>
              <div style={{ height: 6, border: '1px solid var(--ink)', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'var(--ink)', width: `${sent ? back / sent * 100 : 0}%`, transition: 'width 400ms ease' }} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ border: '1px solid var(--ink)', display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0 }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--ink)', background: 'var(--paper-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>INBOX · MOST RECENT FIRST</div>
          <div className="mono tiny" style={{ color: 'var(--ink-3)' }}>{totalBack} replies captured</div>
        </div>
        <div style={{ height: 340, overflow: 'auto' }}>
          {totalBack === 0 && (
            <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--ink-4)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.06em' }}>
              WAITING · FIRST REPLIES TYPICALLY ARRIVE WITHIN MOMENTS…
            </div>
          )}
          {[...arrived].reverse().map((id) => {
            const c = contacts.find((x) => x.id === id);
            const r = replies.find((x) => x.contactId === id);
            if (!c || !r) return null;
            return <ReplyRow key={id} contact={c} reply={r} />;
          })}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={onReset} className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' }}>↺ Reset simulation</button>
        <button onClick={onView} disabled={totalBack < minToView} style={{ padding: '10px 18px', border: '1px solid var(--ink)', background: totalBack < minToView ? 'var(--paper-2)' : 'var(--ink)', color: totalBack < minToView ? 'var(--ink-4)' : 'var(--paper)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: totalBack < minToView ? 'not-allowed' : 'pointer' }}>
          {allBack ? 'Done ✓' : 'Save & close →'}
        </button>
      </div>
    </div>
  );
}

/* ---------- Container ---------- */
export function SurveyFlow({ onClose }) {
  const [stage, setStage] = useState('compose');
  const [subject, setSubject] = useState(sd.emailSubject);
  const [body, setBody] = useState(sd.emailBody);
  const [contacts, setContacts] = useState(() => sd.contacts.slice());
  const [selected, setSelected] = useState(() => new Set(sd.contacts.map((c) => c.id)));
  const [arrived, setArrived] = useState(() => new Set());
  const [replySpeed, setReplySpeed] = useState(() => { const s = +localStorage.getItem('pe-survey-speed'); return s > 0 ? s : 4; });

  const addContact = useCallback((c) => {
    setContacts((prev) => prev.find((x) => x.id === c.id) ? prev : [...prev, c]);
    setSelected((prev) => { const n = new Set(prev); n.add(c.id); return n; });
  }, []);

  const sentList = useMemo(() => contacts.filter((c) => selected.has(c.id)), [contacts, selected]);

  useEffect(() => {
    const h = (e) => {
      if (e.data?.type === '__pe_survey_speed') setReplySpeed(+e.data.value || 1);
      if (e.data?.type === '__pe_survey_reset') doReset();
    };
    window.addEventListener('message', h);
    return () => window.removeEventListener('message', h);
  }, []);

  useEffect(() => {
    if (stage !== 'tracker') return;
    if (arrived.size >= sentList.length) return;
    const remaining = sentList.filter((c) => !arrived.has(c.id));
    if (remaining.length === 0) return;
    const next = remaining[0];
    const delay = (900 + Math.random() * 500) / replySpeed;
    const t = setTimeout(() => { setArrived((prev) => new Set(prev).add(next.id)); }, delay);
    return () => clearTimeout(t);
  }, [stage, arrived, sentList, replySpeed]);

  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const doReset = useCallback(() => { setArrived(new Set()); setStage('compose'); }, []);
  const goToResults = useCallback(() => {
    localStorage.setItem('pe-survey-arrived', JSON.stringify([...arrived]));
    onClose();
  }, [arrived, onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(17,18,20,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: 32 }}
      onClick={onClose}
    >
      <div
        style={{ width: 'min(1080px, 100%)', height: 'min(820px, calc(100vh - 64px))', background: 'var(--paper)', border: '1px solid var(--ink)', boxShadow: '0 24px 80px rgba(17,18,20,0.35)', display: 'grid', gridTemplateRows: 'auto auto 1fr' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--ink)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
          <div>
            <div className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>02 · BRAND BUILDER · SURVEY FLOW</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 24, letterSpacing: '-0.01em', lineHeight: 1.05, marginTop: 4 }}>Run the Character Survey</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="mono tiny" style={{ color: 'var(--ink-3)' }}>SPEED · {replySpeed}×</span>
            <button onClick={onClose} style={{ width: 30, height: 30, border: '1px solid var(--ink)', background: 'var(--paper)', fontSize: 16, lineHeight: 1, cursor: 'pointer' }}>×</button>
          </div>
        </div>

        <ProgressRail stage={stage} />

        <div style={{ padding: '18px 22px 22px', overflow: 'auto', minHeight: 0 }}>
          {stage === 'compose' && (
            <ComposeStage subject={subject} setSubject={setSubject} body={body} setBody={setBody} onNext={() => setStage('send')} />
          )}
          {stage === 'send' && (
            <SendStage contacts={contacts} addContact={addContact} selected={selected} setSelected={setSelected} onBack={() => setStage('compose')} onSend={() => { setArrived(new Set()); setStage('tracker'); }} />
          )}
          {stage === 'tracker' && (
            <TrackerStage contacts={contacts} selected={selected} replies={sd.replies} replySpeed={replySpeed} arrived={arrived} onView={goToResults} onReset={doReset} />
          )}
        </div>
      </div>
    </div>
  );
}

export default SurveyFlow;
